/*
 * Copyright (c) 2021 OCLC, Inc. All Rights Reserved.
 *
 * OCLC proprietary information: the enclosed materials contain proprietary information of OCLC Online Computer
 * Library Center, Inc. and shall not be disclosed in whole or in any part to any third party or used by any person
 * for any purpose, without written consent of OCLC, Inc.  Duplication of any portion of these materials shall include
 * this notice.
 */
import React, { useState, useCallback, useContext, useRef } from 'react'
import { useIntl } from 'react-intl'
import { useHistory } from 'react-router'
import { makeStyles } from '@material-ui/core/styles'
import MuiLink from '@material-ui/core/Link'
import { Box, Card, Typography, CardContent, Button, IconButton, InputAdornment, TextField } from '@material-ui/core'
import Visibility from '@material-ui/icons/Visibility'
import VisibilityOff from '@material-ui/icons/VisibilityOff'
import AlertNotification from './AlertNotification'
import { AuthenticationContext } from '../contexts/AuthenticationContext'
import { login } from '../services/AuthenticationService'

const useCustomStyles = makeStyles((theme) => ({
  card: {
    [theme.breakpoints.down('xs')]: {
      height: '100%',
    },
  },
  cardHeader: {
    background: theme.palette.modalHeader,
    borderBottom: `1px solid ${theme.palette.modalGrayBorder}`,
  },
  cardHolder: {
    width: '378px',
    padding: theme.spacing(25, 0),
    margin: 'auto',
    [theme.breakpoints.down('xs')]: {
      margin: 0,
      width: '100%',
      minWidth: 0,
      padding: 0,
      '& .MuiPaper-rounded': {
        borderRadius: 0,
      },
    },
  },
  cardContent: {
    textAlign: 'left',
    justifyContent: 'center',
    display: 'flex',
    padding: theme.spacing(6, 0, 4),
    '&.MuiCardContent-root:last-child': {
      paddingBottom: theme.spacing(4),
    },
  },
  button: {
    height: 40,
    width: 292,
    textTransform: 'none',
  },
  buttonBox: {
    textAlign: 'center',
  },
  accountLink: {
    display: 'inline-flex',
    justifyContent: 'center',
  },
  inputTextField: {
    width: 292,
  },
  inputTextFieldError: {
    width: 292,
    '& .MuiOutlinedInput-root': {
      border: `2px solid ${theme.palette.notificationError.main}`,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none !important',
    },
  },
  label: {
    display: 'block',
    marginBottom: theme.spacing(1),
  },
  flexCenter: {
    display: 'flex',
    justifyContent: 'center',
  },
  newUser: {
    fontWeight: 700,
    alignSelf: 'center',
    paddingRight: theme.spacing(0.5),
  },
  iconFocus: {
    '&:focus': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
  },
  alertBox: {
    width: 292,
    marginBottom: theme.spacing(4),
  },
}))

/**
 * The footer.
 * @returns {Node} - A react Node.
 */
function SignInCard() {
  const classes = useCustomStyles()
  const intl = useIntl()
  const history = useHistory()
  const usernameRef = useRef()
  const passwordRef = useRef()
  const authData = useContext(AuthenticationContext)

  // prettier-ignore
  const messages = {
    createAccount: intl.formatMessage({id: 'create_an_account', defaultMessage: 'Create an account'}),
    usernameLabel: intl.formatMessage({id: 'username', defaultMessage: 'Username'}),
    passwordLabel: intl.formatMessage({id: 'password', defaultMessage: 'Password'}),
    enterPassword: intl.formatMessage({id: 'error_no_password', defaultMessage: 'Enter a password for your account.'}),
    enterUsername: intl.formatMessage({id: 'error_no_username', defaultMessage: 'Enter a username for your account.'}),
    invalidAuth: intl.formatMessage({id: 'error_auth_invalid', defaultMessage: 'Authentication failed. The username or password is invalid.'}),
    retry: intl.formatMessage({id: 'error_retry', defaultMessage: 'Try again. A system error occurred.'}),
    signIn: intl.formatMessage({id: 'sign_in', defaultMessage: 'Sign in.'}),
    forgotPassword: intl.formatMessage({id: 'forgot_password', defaultMessage: 'Forgot password'}),
  }

  const [values, setValues] = useState({
    username: '',
    password: '',
    showPassword: false,
  })
  const [errors, setErrors] = useState({
    password: false,
    username: false,
  })
  const [showAlerts, setShowAlerts] = useState({
    retry: false,
    invalid: false,
  })

  const goToChangePasswordPage = useCallback(() => {
    history.push('/changepassword')
  }, [history])

  const submitForm = useCallback(
    async (e) => {
      e.preventDefault()
      setShowAlerts({ retry: false, invalid: false })
      if (!values.password) setErrors({ ...errors, password: true })
      if (!values.username) setErrors({ ...errors, username: true })

      if (values.password && values.username) {
        const response = await login(values)
        if (response.status === 200 && response.auth) {
          authData.setAuthData(true)
          goToChangePasswordPage()
        } else if (response.status === 401) {
          setShowAlerts({ ...showAlerts, invalid: true })
          setValues({ username: '', password: '', showPassword: false })
          usernameRef.current.value = ''
          passwordRef.current.value = ''
        } else {
          setShowAlerts({ ...showAlerts, retry: true, invalid: false })
          setValues({ username: '', password: '', showPassword: false })
          usernameRef.current.value = ''
          passwordRef.current.value = ''
        }
      }
    },
    [goToChangePasswordPage, values.username, values.password],
  )

  const handleChange = (prop) => (event) => {
    setValues({ ...values, [prop]: event.target.value })
    if (event.target.value.length > 0) setErrors({ ...errors, [prop]: false })
  }

  const handleClickShowPassword = (event) => {
    event.preventDefault()
    setValues({ ...values, showPassword: !values.showPassword })
  }

  const handleMouseDownPassword = (event) => {
    event.preventDefault()
  }

  const createAlert = (testId, text) => {
    return (
      <Box className={classes.alertBox}>
        <AlertNotification variant='error' id={testId}>
          {text}
        </AlertNotification>
      </Box>
    )
  }

  const createInputError = (testId, text) => {
    return (
      <AlertNotification variant='error' id={testId} alertLevel='inline'>
        {text}
      </AlertNotification>
    )
  }

  const invalidAlert = createAlert('login-invalid-alert', messages.invalidAuth)
  const retryAlert = createAlert('login-retry-alert', messages.retry)
  const noUserNameError = createInputError('no-username-error', messages.enterUsername)
  const noPasswordError = createInputError('no-password-error', messages.enterPassword)

  const signInForm = (
    <>
      <form id='sign-in-form' data-testid='sign-in-form' className={classes.form}>
        <Box mb={2.5}>
          {showAlerts.invalid ? invalidAlert : showAlerts.retry ? retryAlert : null}
          <Typography component='label' variant='body2' htmlFor='sign-in-username' className={classes.label}>
            <strong>{messages.usernameLabel}</strong>
          </Typography>
          <TextField
            inputRef={usernameRef}
            className={errors.username ? classes.inputTextFieldError : classes.inputTextField}
            id='sign-in-username'
            data-testid='sign-in-username'
            size='small'
            variant='outlined'
            fullWidth
            color={errors.username ? 'secondary' : 'primary'}
            required
            defaultValue={''}
            inputProps={{ maxLength: 20 }}
            onChange={handleChange('username')}
          />
          {errors.username ? noUserNameError : null}
        </Box>
        <Box mb={4}>
          <Typography component='label' variant='body2' htmlFor='sign-in-pwd' className={classes.label}>
            <strong>{messages.passwordLabel}</strong>
          </Typography>
          <TextField
            inputRef={passwordRef}
            className={errors.password ? classes.inputTextFieldError : classes.inputTextField}
            id='sign-in-pwd'
            data-testid='sign-in-pwd'
            size='small'
            variant='outlined'
            fullWidth
            color={errors.password ? 'secondary' : 'primary'}
            required
            defaultValue={''}
            inputProps={{ maxLength: 25 }}
            onChange={handleChange('password')}
            type={values.showPassword ? 'text' : 'password'}
            InputProps={{
              endAdornment: (
                <InputAdornment position='end'>
                  <IconButton
                    aria-label={intl.formatMessage({
                      id: 'toggle_password_visibility',
                      defaultMessage: 'toggle password visibility',
                    })}
                    onClick={handleClickShowPassword}
                    onMouseDown={handleMouseDownPassword}
                    edge='end'
                    className={classes.iconFocus}>
                    {values.showPassword ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          {errors.password ? noPasswordError : null}
        </Box>
        <Box mb={2} className={classes.buttonBox}>
          <Button
            color='primary'
            variant='contained'
            className={classes.button}
            type='submit'
            data-testid='sign-in-button'
            onClick={submitForm}>
            {messages.signIn}
          </Button>
        </Box>
        <Box mb={4} className={classes.flexCenter}>
          <MuiLink
            className={classes.accountLink}
            data-testid='forgot-password-link'
            underline='none'
            target='_blank'
            href={'/forgot'}>
            {messages.forgotPassword}
          </MuiLink>
        </Box>
        <Box className={classes.flexCenter}>
          <Typography className={classes.newUser}>New User?</Typography>
          <MuiLink
            className={classes.accountLink}
            data-testid='create-account-link'
            underline='none'
            target='_blank'
            href={'/create'}>
            {messages.createAccount}
          </MuiLink>
        </Box>
      </form>
    </>
  )

  return (
    <Box className={classes.cardHolder}>
      <Card elevation={9} className={classes.card}>
        <CardContent className={classes.cardContent}>{signInForm}</CardContent>
      </Card>
    </Box>
  )
}

export default SignInCard
